import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type {
  Occupancy,
  Override,
  Period,
  Restriction,
  RestrictionRecommendation,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { RestrictionOverridePayload } from 'lumen/services/repositories/restriction-action';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  DAY_PRICING_MODAL_TAB_IDS,
  type DayPricingModalTabIds,
  OVERRIDE_TYPES,
  RESTRICTION_MODEL_CONTEXTS,
  RESTRICTION_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import type {
  MinimalRestrictionAction,
  RestrictionActionValue,
} from 'lumen/utils/constants/restriction-actions';
import { calculateTotalRoomsForSale } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

import styles from './day-override-modal.module.css';
import OverbookingPanel from './overbooking-panel';
import RestrictionsPanel from './restrictions-panel';
import Sidebar, { type SidebarItem } from './sidebar';

interface HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature {
  Args: {
    onCloseModal: () => void;
    period: Period;
  };
  Blocks: {
    default: [];
  };
}

export default class DayOverrideModal extends Component<HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature> {
  @service declare dates: Services['dates'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service('restrictions/action-poller')
  declare restrictionsActionPoller: Services['restrictions/action-poller'];
  @service('restrictions/data-request-service')
  declare restrictionsDataRequestService: Services['restrictions/data-request-service'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeTab: DayPricingModalTabIds =
    DAY_PRICING_MODAL_TAB_IDS.OVERBOOKING;
  @tracked automationOn: boolean = false;
  @tracked overbooking: number | null = null;
  @tracked restrictionAutomationOn: boolean = false;

  /* Snapshot of hasOverbookingOverride at modal open.
     Read live it would flip after the delete + re-request, breaking overrideRemoved mid-send. */
  initiallyHadOverride: boolean = false;

  get hasOverbookingOverride(): boolean {
    return this.args.period.hasOverbookingOverride;
  }

  get hotelLevelRestriction(): Restriction | undefined {
    return this.args.period.hotelLevelRestriction;
  }

  get hotelLevelRestrictionRecommendation():
    | RestrictionRecommendation
    | undefined {
    return this.args.period.hotelLevelRestrictionRecommendation;
  }

  get isOverbooked(): boolean {
    return (
      typeof this.physicalRoomsAvailable === 'number' &&
      this.physicalRoomsAvailable < 0
    );
  }

  get isSending(): boolean {
    const dateKey = this.args.period.fromDate.format('YYYY-MM-DD');
    const isSending =
      !!this.restrictionsActionPoller.datesBeingRefreshed[dateKey] ||
      this.restrictionsDataRequestService.createRestrictionActionTask.isRunning;
    const isDeletingOverride =
      this.pricingAssistantDataRequestService.deleteOverrideTask.isRunning;
    const isReRequestingData =
      this.pricingAssistantDataRequestService.restrictionsRequestTask
        .isRunning ||
      this.pricingAssistantDataRequestService
        .restrictionsRecommendationsRequestTask.isRunning;

    return isSending || isDeletingOverride || isReRequestingData;
  }

  get modalTitle(): string {
    const carate = this.args.period.carates?.[0];
    const day = carate?.period.fromDate.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );

    return `${this.intl.t('general.edit')} ${day}, ${this.intl.t('pricing_assistant.all_rooms')}`;
  }

  get occupancyData(): Occupancy | undefined {
    return this.args.period.day.occupancy;
  }

  /* True when the user has toggled automation ON from an initial OFF state,
     meaning the existing override will be deleted on Send. */
  get overrideRemoved(): boolean {
    return this.automationOn && this.initiallyHadOverride;
  }

  get physicalRoomsAvailable(): number | string {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return this.occupancyData.availableRooms;
    }

    return this.intl.t('general.missing');
  }

  get physicalRoomsAvailableLabel(): string {
    if (
      this.occupancyData &&
      this.occupancyData.inventoryExcludingOOORooms !== null
    ) {
      return this.intl.t(
        'pricing_manager.overbooking.physical_rooms_available_tooltip',
        {
          outOfOrder: this.occupancyData.roomsOutOfOrder,
        },
      );
    }

    return this.intl.t(
      'pricing_manager.overbooking.physical_rooms_available_tooltip',
      {
        outOfOrder: this.intl.t('general.missing'),
      },
    );
  }

  get restrictionsTabEnabled(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.DAY_OVERRIDE_MODAL_RESTRICTIONS_TAB,
    );
  }

  get roomsForSaleValue(): string | number {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return calculateTotalRoomsForSale(
        this.occupancyData.availableRooms,
        this.overbooking ?? 0, // Treat empty overbooking (null) as 0 for calculation purposes
      );
    }

    return this.intl.t('general.missing');
  }

  get sidebarItems(): SidebarItem[] {
    return [
      {
        id: DAY_PRICING_MODAL_TAB_IDS.OVERBOOKING,
        label: this.intl.t('pricing_manager.overbooking.sidebar_label'),
      },
      {
        id: DAY_PRICING_MODAL_TAB_IDS.RESTRICTIONS,
        label: this.intl.t('pricing_manager.restrictions.sidebar_label'),
      },
    ];
  }

  constructor(
    owner: Owner,
    args: HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature['Args'],
  ) {
    super(owner, args);
    this.overbooking = this.args.period.effectiveOverbookingWithOverride;
    this.initiallyHadOverride = this.args.period.hasOverbookingOverride;
    this.automationOn = !this.initiallyHadOverride;
    this.restrictionAutomationOn = !this.args.period.hasMinLosOverride;
  }

  private getOverbookingRestrictionValues(): RestrictionActionValue[] | null {
    const recommendationValue =
      this.hotelLevelRestrictionRecommendation?.overbookingValue;

    // No recommendation — nothing to send
    if (recommendationValue == null) {
      return null;
    }

    // Override removed — send the recommendation value without an override
    if (this.overrideRemoved) {
      return [
        { type: RESTRICTION_TYPES.OVERBOOKING, value: recommendationValue },
      ];
    }

    if (this.overbooking == null) {
      return null;
    }

    return [{ type: RESTRICTION_TYPES.OVERBOOKING, value: this.overbooking }];
  }

  private async sendOverbooking(): Promise<'success' | 'error' | null> {
    const overbookingOverride = this.hotelLevelRestriction?.overbookingOverride;
    const formattedDate = this.args.period.fromDate.format('YYYY-MM-DD');
    const hotelId = this.hotelAndMarketService.hotel.id!;

    if (this.overrideRemoved && overbookingOverride) {
      const result =
        await this.pricingAssistantDataRequestService.deleteOverrideTask.perform(
          overbookingOverride.id,
        );

      if (!result?.isSuccess) {
        return 'error';
      }

      // Pre-unload so stale records don't persist if absent from the re-request response
      [...this.args.period.restrictions].forEach((r) =>
        this.store.unloadRecord(r),
      );
      [...this.args.period.restrictionRecommendations].forEach((r) =>
        this.store.unloadRecord(r),
      );

      await Promise.all([
        this.pricingAssistantDataRequestService.restrictionsRequestTask.perform(
          hotelId,
          formattedDate,
          formattedDate,
        ),
        this.pricingAssistantDataRequestService.restrictionsRecommendationsRequestTask.perform(
          hotelId,
          formattedDate,
          formattedDate,
        ),
      ]);
    }

    const shouldSendOverride = !this.overrideRemoved;

    const override: RestrictionOverridePayload | undefined =
      shouldSendOverride && this.overbooking !== null
        ? {
            subscription_id: parseInt(hotelId, 10),
            from_date: formattedDate,
            value: this.overbooking,
            type: OVERRIDE_TYPES.OVERBOOKING,
            context: RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
          }
        : undefined;

    // Called by the poller after the push completes to refresh override data
    this.restrictionsDataRequestService.overrideCallbackOnPushFinished = async (
      day,
      subscriptionId,
    ): Promise<void> => {
      this.store
        .peekAll<Override>('override')
        .filter((o) => o.fromDate.format('YYYY-MM-DD') === day)
        .forEach((o) => this.store.unloadRecord(o));

      await this.pricingAssistantDataRequestService.requestOverridesTask.perform(
        subscriptionId,
        day,
        day,
      );
    };

    const restrictionValues = this.getOverbookingRestrictionValues();
    const restrictionData: MinimalRestrictionAction | undefined =
      restrictionValues !== null
        ? { from_date: formattedDate, values: restrictionValues }
        : undefined;

    if (this.overrideRemoved && !restrictionData) {
      // Override deleted, no recommendation to push — done
      return 'success';
    }

    if (!restrictionData && !override) {
      // Nothing to send
      return null;
    }

    try {
      await this.restrictionsDataRequestService.sendRestriction(
        restrictionData,
        RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
        override,
      );
      return 'success';
    } catch {
      return 'error';
    }
  }

  @action
  onCloseModal(): void {
    this.args.onCloseModal();
  }

  @action
  async onSendOverbooking(): Promise<void> {
    if (this.isSending) {
      return;
    }

    // Automation is on and there's no override to remove — nothing to do
    if (this.automationOn && !this.overrideRemoved) {
      this.onCloseModal();
      return;
    }

    this.usageAnalytics.track(
      this.overrideRemoved
        ? 'Autopilot Override Disabled'
        : 'Autopilot Override Enabled',
      {
        eventTrigger: 'day-override-modal',
        product: 'pricing-manager',
        overrideType: 'overbooking',
      },
    );

    const outcome = await this.sendOverbooking();

    if (outcome === 'success') {
      this.toast.success(this.intl.t('general.changes_saved'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    } else if (outcome === 'error') {
      this.toast.error(this.intl.t('general.error_short'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    }

    this.onCloseModal();
  }

  @action
  selectTab(id: DayPricingModalTabIds): void {
    if (
      [
        DAY_PRICING_MODAL_TAB_IDS.OVERBOOKING,
        DAY_PRICING_MODAL_TAB_IDS.RESTRICTIONS,
      ].includes(id)
    ) {
      this.activeTab = id;
    }
  }

  @action
  toggleAutomation(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.automationOn = checked;

    if (checked && this.initiallyHadOverride) {
      // Turning automation ON when override exists — show recommendation value
      this.overbooking = this.args.period.effectiveOverbooking;
    } else if (!checked) {
      // Turning automation OFF — restore the value from when the modal opened
      this.overbooking = this.args.period.effectiveOverbookingWithOverride;
    }
  }

  @action
  toggleRestrictionAutomation(event: Event): void {
    this.restrictionAutomationOn = (event.target as HTMLInputElement).checked;
  }

  @action
  updateOverbooking(value: number | null): void {
    this.overbooking = value;
  }

  <template>
    <PrismModal
      @onClose={{this.onCloseModal}}
      @title={{this.modalTitle}}
      class={{if this.restrictionsTabEnabled styles.modal}}
    >
      <:default>
        {{#if this.restrictionsTabEnabled}}
          <div class={{styles.body}}>
            <div class="text-300--regular">
              <Sidebar
                @activeId={{this.activeTab}}
                @items={{this.sidebarItems}}
                @onSelect={{this.selectTab}}
              />
            </div>
            <div>
              {{#if (eq this.activeTab DAY_PRICING_MODAL_TAB_IDS.OVERBOOKING)}}
                <OverbookingPanel
                  @automationOn={{this.automationOn}}
                  @effectiveOverbooking={{@period.effectiveOverbooking}}
                  @hasOverbookingOverride={{this.hasOverbookingOverride}}
                  @isOverbooked={{this.isOverbooked}}
                  @onToggleAutomation={{this.toggleAutomation}}
                  @onUpdateOverbooking={{this.updateOverbooking}}
                  @overbooking={{this.overbooking}}
                  @physicalRoomsAvailable={{this.physicalRoomsAvailable}}
                  @physicalRoomsAvailableLabel={{this.physicalRoomsAvailableLabel}}
                  @roomsForSaleValue={{this.roomsForSaleValue}}
                />
              {{else if
                (eq this.activeTab DAY_PRICING_MODAL_TAB_IDS.RESTRICTIONS)
              }}
                <RestrictionsPanel
                  @automationOn={{this.restrictionAutomationOn}}
                  @effectiveMinLos={{@period.effectiveMinLos}}
                  @hasMinLosOverride={{@period.hasMinLosOverride}}
                  @onToggleAutomation={{this.toggleRestrictionAutomation}}
                />
              {{/if}}
            </div>
          </div>
        {{else}}
          <OverbookingPanel
            @automationOn={{this.automationOn}}
            @effectiveOverbooking={{@period.effectiveOverbooking}}
            @hasOverbookingOverride={{this.hasOverbookingOverride}}
            @isOverbooked={{this.isOverbooked}}
            @onToggleAutomation={{this.toggleAutomation}}
            @onUpdateOverbooking={{this.updateOverbooking}}
            @overbooking={{this.overbooking}}
            @physicalRoomsAvailable={{this.physicalRoomsAvailable}}
            @physicalRoomsAvailableLabel={{this.physicalRoomsAvailableLabel}}
            @roomsForSaleValue={{this.roomsForSaleValue}}
          />
        {{/if}}
      </:default>
      <:footer>
        <div
          class="mx-500 flex h-full flex-row items-center justify-end gap-200"
        >
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{this.onCloseModal}}
          />
          <PrismButton
            @buttonType="primary"
            @iconBefore="send"
            @isDisabled={{eq
              this.activeTab
              DAY_PRICING_MODAL_TAB_IDS.RESTRICTIONS
            }}
            @isLoading={{this.isSending}}
            @label={{t "pricing_assistant.label.push"}}
            @onClick={{this.onSendOverbooking}}
            data-test-id="overbooking-send-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
